import {Injectable} from "@angular/core";
import {CoreBaseEffects} from "@core/core-base.effects";
import {Action, Store} from "@ngrx/store";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Observable} from "rxjs";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {
  FileDto,
  IssueListDto,
  IssueTemplateListDto,
  IssueType,
} from "@server-models";
import {Photo} from "@capacitor/camera";
import {SharedIssueBaseActions} from "@shared/components/issues/store/shared-issue-base.action-type";
import {
  TGetIssue
} from "@shared/components/issues/store/shared-issue-base.actions";
import {FormGroup} from "@angular/forms";
import {TCurrentIssueDto} from "@shared/components/issues/types/issue-dto.type";
import {TSubmitIssueDto} from "@shared/components/issues/types/submit-issue-dto.type";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";

@Injectable({
  providedIn: 'root'
})
export abstract class SharedIssueBaseEffects extends CoreBaseEffects {

  protected constructor(
    store: Store,
    rawActions$: Actions
  ) {
    super(store, rawActions$);
  }

  abstract getItemsPaginated(action$: Observable<ISharedLocalBodyRequest>): Observable<ILocalPageOutputDto<IssueListDto[]> | any>;

  abstract getItemsTemplate(action$: Observable<{
    templateId?: number;
    resourceId?: number;
    pageNumber?: number;
  }>): Observable<ILocalPageOutputDto<IssueTemplateListDto[]> | any>;

  abstract getIssueTemplateListSuccess(action$: Observable<{
    templateId?: number;
    resourceId?: number;
  }>): Observable<ILocalPageOutputDto<IssueTemplateListDto[]> | any>;

  abstract getIssuePreview(action$: Observable<ReturnType<typeof SharedIssueBaseActions.getIssuePreview>>): Observable<(unknown | any)>;

  abstract getIssuePreviewSuccess(action$: Observable<{
    data: unknown,
    isRefresh?: boolean
  }>): Observable<any>;

  abstract getIssueByIssueType(action$: Observable<TGetIssue>): Observable<unknown | any>;

  abstract getByIdSuccess(action$: Observable<{
    data: TCurrentIssueDto
  }>): Observable<Action<string>>;

  abstract prepareFormsToSendTemplatePreview(action$: Observable<{
    form: FormGroup,
    templatePreviewDto: any
  }>): Observable<any>;

  abstract prepareFormsToSendTemplatePreviewDone(action$: Observable<{
    submitReady: TSubmitIssueDto
  }>): Observable<any>;

  abstract sendTemplatePreview(action$: Observable<{
    issuePrepared: TSubmitIssueDto
  }>): Observable<any>;

  abstract formatReadDetailData(action$: Observable<Action<string>>): Observable<{
    detailDataFormatted: ILocalIssueDetailData
  } | any>;

  abstract formatReadDetailDataFinish(action$: Observable<{
    detailDataFormatted: ILocalIssueDetailData
  }>): Observable<any>;

  abstract askPictureInput(action$: Observable<{ isLoadingId: number | string }>): Observable<{ picture: Photo } | any>;

  abstract askPictureInputSuccess(action$: Observable<{ picture: Photo }>): Observable<{ picture: Photo } | any>;

  abstract reducePicture(action$: Observable<{ picture: Photo }>): Observable<{ blob: Blob } | any>;

  abstract reducePictureSuccess(action$: Observable<{ blob: Blob }>): Observable<{
    blob: Blob,
    fileType: string
  } | any>;

  abstract savePicture(action$: Observable<{ blob: Blob; fileType: string }>): Observable<{ picture: FileDto } | any>;

  abstract convertDateURLToBlob(action$: Observable<{ isLoadingId: number | string; dataUrl: string }>): Observable<{
    blob: Blob
  } | any>;

  abstract convertDateURLToBlobSuccess(action$: Observable<{ blob: Blob; dataUrl: string; index: number | string }>): Observable<{
    blob: Blob
  } | any>;

  abstract saveSignature(action$: Observable<{ blob: Blob; index: number | string }>): Observable<{ image: FileDto } | any>;

  getItemsPaginated$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.getIssueListPaginated),
    action => this.getItemsPaginated(action)
  ));

  getItemsTemplate$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.getIssueTemplateList),
    action => this.getItemsTemplate(action)
  ));

  getIssueTemplateListSuccess$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.getIssueTemplateListSuccess),
    action => this.getIssueTemplateListSuccess(action)
  ));

  getIssuePreview$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.getIssuePreview),
    action => this.getIssuePreview(action)
  ));

  getIssuePreviewSuccess$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.getIssuePreviewSuccess),
    action => this.getIssuePreviewSuccess(action)
  ));

  getIssueByIssueType$ = createEffect(() => this.actions().pipe(
    ofType<TGetIssue>(SharedIssueBaseActions.getIssueByIssueType),
    action => this.getIssueByIssueType(action)
  ));

  getByIdSuccess$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.getByIdSuccess),
    action => this.getByIdSuccess(action)
  ));

  prepareFormsToSendTemplatePreview$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.prepareFormsToSendTemplatePreview),
    action => this.prepareFormsToSendTemplatePreview(action)
  ));

  prepareFormsToSendTemplatePreviewDone$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.prepareFormsToSendTemplatePreviewDone),
    action => this.prepareFormsToSendTemplatePreviewDone(action)
  ));

  sendTemplatePreview$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.sendTemplatePreview),
    action => this.sendTemplatePreview(action)
  ));

  formatDetailData$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.formatReadDetailData),
    action => this.formatReadDetailData(action)
  ));

  formatReadDetailDataFinish$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.formatReadDetailDataFinish),
    action => this.formatReadDetailDataFinish(action)
  ), { dispatch: false });

  askPictureInput$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.askPictureInput),
    action => this.askPictureInput(action)
  ));

  askPictureInputSuccess$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.askPictureInputSuccess),
    action => this.askPictureInputSuccess(action)
  ));

  reducePicture$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.reducePicture),
    action => this.reducePicture(action)
  ));

  reducePictureSuccess$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.reducePictureSuccess),
    action => this.reducePictureSuccess(action)
  ));

  savePicture$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.savePicture),
    action => this.savePicture(action)
  ));

  convertDateURLToBlob$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.convertDateURLToBlob),
    action => this.convertDateURLToBlob(action)
  ));

  convertDateURLToBlobSuccess$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.convertDateURLToBlobSuccess),
    action => this.convertDateURLToBlobSuccess(action)
  ));

  saveSignature$ = createEffect(() => this.actions().pipe(
    ofType(SharedIssueBaseActions.saveSignature),
    action => this.saveSignature(action)
  ));

}
